<template>
  <div>
    <h1 class="text-3xl font-bold mb-6 text-center text-gray-800">1. Provide Questions as Text</h1>
    <p class="text-center text-gray-600 mb-4">Copy and paste the questions into the textbox below.</p>

    <!-- Buttons to switch input method -->
    <div class="flex justify-center space-x-4 mb-6">
      <button @click="goToUpload" class="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition">
        Upload from Computer
      </button>
      <button @click="goToQR" class="bg-indigo-500 text-white px-4 py-2 rounded hover:bg-indigo-600 transition">
        Take Picture with Phone
      </button>
    </div>

    <div v-if="navLoading" class="flex justify-center items-center mb-4">
      <svg class="animate-spin h-5 w-5 mr-3 text-blue-500" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V4a10 10 0 00-10 10h2zm0 0a8 8 0 018 8v-2a6 6 0 00-6-6H4z"></path>
      </svg>
      <span>Saving questions...</span>
    </div>

    <div v-if="errorMessage" class="text-red-500 text-center mb-4">{{ errorMessage }}</div>

    <textarea
        v-model="questionsContent"
        class="w-full border border-gray-300 bg-white rounded-lg p-4 overflow-y-auto"
        rows="15"
        placeholder="Copy and Paste the questions here..."
        ref="textarea"
        @input="adjustTextareaHeight"
        :disabled="navLoading"
    ></textarea>

    <div class="flex justify-end mt-4">
      <button @click="applyChanges" class="bg-gray-700 text-white px-4 py-2 rounded hover:bg-gray-800 transition">Save Changes</button>
    </div>

    <!-- Navigation Buttons -->
    <div class="flex justify-between mt-8">
      <button
          @click="goBack"
          class="bg-gray-500 text-white px-4 py-2 rounded transition hover:bg-gray-600 flex items-center min-w-[120px] justify-center"
          :disabled="navLoading"
      >
        Back
      </button>

      <button
          @click="triggerNext"
          class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition flex items-center min-w-[120px] justify-center"
          :disabled="navLoading">
        <svg v-if="navLoading" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V4a10 10 0 00-10 10h2zm0 0a8 8 0 018 8v-2a6 6 0 00-6-6H4z"></path>
        </svg>
        <span v-if="navLoading">Processing...</span>
        <span v-else>{{ buttonText }}</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import axios from '../services/axiosInstance'
import { useModalStore } from '@/stores/modal'

const questionsContent = ref('')
const navLoading = ref(false)
const errorMessage = ref('')
const textarea = ref(null)
const router = useRouter()
const route = useRoute()
const modal = useModalStore()

const buttonText = computed(() => 'Next')

const fetchQuestions = async () => {
  const testId = route.params.testId
  if (!testId) return
  try {
    const response = await axios.get(`/tests/${testId}/questions`)
    questionsContent.value = response.data.content || ''
    nextTick(() => adjustTextareaHeight())
  } catch (error) {
    console.error('Error fetching questions content:', error)
  }
}

const applyChanges = async () => {
  const testId = route.params.testId
  if (!testId) return
  try {
    await axios.put(`/tests/${testId}/questions`, { content: questionsContent.value })
    modal.success('Questions saved successfully!')
  } catch (error) {
    console.error('Error saving questions content:', error)
    modal.error('Failed to save changes.')
  }
}

const goToUpload = () => {
  router.push({ name: 'QuestionsUpload', params: { testId: route.params.testId }, query: route.query })
}
const goToQR = () => {
  router.push({ name: 'QuestionsQR', params: { testId: route.params.testId }, query: route.query })
}

const adjustTextareaHeight = () => {
  if (textarea.value) {
    textarea.value.style.height = 'auto'
    textarea.value.style.height = `${textarea.value.scrollHeight}px`
  }
}

const goBack = async () => {
  const isNewTest = route.query.isNew === 'true'
  const testId = route.params.testId
  if (isNewTest) {
    const confirmed = await modal.confirm(
      'Are you sure you want to cancel? The new test will be deleted.',
      'Cancel Creation',
      'Yes, Delete',
      'No, Keep Editing'
    )

    if (confirmed) {
      axios.delete(`/tests/${testId}`)
        .then(() => router.push('/testsoverview'))
        .catch(err => {
          console.error('Failed to delete test:', err)
          modal.error('Could not delete test. Please do so manually.')
          router.push('/testsoverview')
        })
    }
  } else {
    router.push(`/tests/${testId}`)
  }
}

const triggerNext = async () => {
  if (!questionsContent.value || !questionsContent.value.trim()) {
    const confirmed = await modal.confirm('The questions text is empty. Are you sure you want to continue?', 'Empty Content')
    if (!confirmed) return
  }

  navLoading.value = true
  const testId = route.params.testId
  try {
    await axios.put(`/tests/${testId}/questions`, { content: questionsContent.value })
    router.push({ name: 'AnswersUpload', params: { testId } })
  } catch (error) {
    errorMessage.value = 'An error occurred while saving the questions. Please try again.'
    console.error('Error in triggerNext:', error)
  } finally {
    navLoading.value = false
  }
}

onMounted(fetchQuestions)
</script>

<style scoped>
textarea { resize: none; max-height: 70vh; }
</style>
